import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AttendanceException,
  AttendanceRecord,
  AttendanceTeamSummary,
} from "@leave-system/shared";
import { MainLayout } from "../components/layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../hooks/useAuth";
import attendanceService from "../services/attendance.service";
import {
  formatAttendanceDateTime,
  formatAttendanceMinutes,
  fromDateTimeLocalValue,
  getAttendanceExceptionStatusClass,
  getAttendanceExceptionStatusLabel,
  getAttendanceExceptionTypeLabel,
  getAttendanceStatusClass,
  getAttendanceStatusLabel,
  getLocalTodayDateInput,
  toDateTimeLocalValue,
} from "../utils/attendance";

type RecordDraft = {
  checkInAt: string;
  checkOutAt: string;
  checkInNote: string;
  checkOutNote: string;
};

type ExceptionDraft = {
  checkInAt: string;
  checkOutAt: string;
  resolutionNote: string;
};

function buildRecordDraft(record: AttendanceRecord | null): RecordDraft {
  return {
    checkInAt: toDateTimeLocalValue(record?.checkInAt),
    checkOutAt: toDateTimeLocalValue(record?.checkOutAt),
    checkInNote: record?.checkInNote || "",
    checkOutNote: record?.checkOutNote || "",
  };
}

function buildExceptionDraft(exception: AttendanceException): ExceptionDraft {
  return {
    checkInAt: toDateTimeLocalValue(
      exception.requestedCheckInAt || exception.attendanceRecord?.checkInAt
    ),
    checkOutAt: toDateTimeLocalValue(
      exception.requestedCheckOutAt || exception.attendanceRecord?.checkOutAt
    ),
    resolutionNote: exception.resolutionNote || "",
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  return (
    ((error as any)?.response?.data?.error?.message as string | undefined) ||
    (error instanceof Error ? error.message : undefined) ||
    fallback
  );
}

export function AttendanceManagementPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(getLocalTodayDateInput());
  const [pageError, setPageError] = useState("");
  const [recordDrafts, setRecordDrafts] = useState<Record<string, RecordDraft>>(
    {}
  );
  const [exceptionDrafts, setExceptionDrafts] = useState<
    Record<string, ExceptionDraft>
  >({});

  const canManageTeam =
    user?.role === "admin" || user?.role === "hr" || user?.role === "manager";

  const teamQuery = useQuery({
    queryKey: ["attendance", "team", selectedDate],
    queryFn: () => attendanceService.getTeamSummary(1, 50, selectedDate),
    enabled: canManageTeam,
  });

  const exceptionsQuery = useQuery({
    queryKey: ["attendance", "exceptions", "team"],
    queryFn: () => attendanceService.getExceptions("team", 1, 25),
    enabled: canManageTeam,
  });

  const refreshAttendance = () => {
    queryClient.invalidateQueries({ queryKey: ["attendance"] });
  };

  const updateRecordMutation = useMutation({
    mutationFn: (payload: {
      recordId: string;
      input: {
        checkInAt?: string;
        checkOutAt?: string;
        checkInNote?: string;
        checkOutNote?: string;
      };
    }) => attendanceService.updateRecord(payload.recordId, payload.input),
    onSuccess: () => {
      setPageError("");
      refreshAttendance();
    },
    onError: (error) => {
      setPageError(getErrorMessage(error, "ไม่สามารถอัปเดตเวลาเข้างานได้"));
    },
  });

  const resolveExceptionMutation = useMutation({
    mutationFn: (payload: {
      exceptionId: string;
      input: {
        status: "resolved" | "rejected";
        resolutionNote?: string;
        checkInAt?: string;
        checkOutAt?: string;
      };
    }) => attendanceService.resolveException(payload.exceptionId, payload.input),
    onSuccess: () => {
      setPageError("");
      refreshAttendance();
    },
    onError: (error) => {
      setPageError(getErrorMessage(error, "ไม่สามารถจัดการคำขอแก้เวลาได้"));
    },
  });

  const getRecordDraft = (summary: AttendanceTeamSummary): RecordDraft => {
    if (!summary.record) {
      return buildRecordDraft(null);
    }
    return recordDrafts[summary.record.id] || buildRecordDraft(summary.record);
  };

  const setRecordDraft = (
    recordId: string,
    field: keyof RecordDraft,
    value: string
  ) => {
    setRecordDrafts((prev) => ({
      ...prev,
      [recordId]: {
        ...(prev[recordId] || buildRecordDraft(null)),
        [field]: value,
      },
    }));
  };

  const getExceptionDraft = (exception: AttendanceException): ExceptionDraft =>
    exceptionDrafts[exception.id] || buildExceptionDraft(exception);

  const setExceptionDraft = (
    exceptionId: string,
    field: keyof ExceptionDraft,
    value: string
  ) => {
    setExceptionDrafts((prev) => ({
      ...prev,
      [exceptionId]: {
        ...(prev[exceptionId] || {
          checkInAt: "",
          checkOutAt: "",
          resolutionNote: "",
        }),
        [field]: value,
      },
    }));
  };

  if (!canManageTeam) {
    return (
      <MainLayout title="จัดการ WFH Attendance">
        <div className="max-w-3xl mx-auto rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          หน้านี้สำหรับ `manager`, `hr` และ `admin` เท่านั้น
        </div>
      </MainLayout>
    );
  }

  const isLoading = teamQuery.isLoading || exceptionsQuery.isLoading;
  const teamItems = teamQuery.data?.items || [];
  const exceptionItems = exceptionsQuery.data?.items || [];
  const pendingExceptions = exceptionItems.filter(
    (item) => item.status === "pending"
  );
  const checkedInCount = teamItems.filter(
    (item) => item.status === "checked_in"
  ).length;
  const completedCount = teamItems.filter(
    (item) => item.status === "completed"
  ).length;
  const missingCheckoutCount = teamItems.filter(
    (item) => item.status === "missing_checkout"
  ).length;

  return (
    <MainLayout title="จัดการ WFH Attendance">
      <div className="max-w-7xl mx-auto space-y-6">
        {pageError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {pageError}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle>ภาพรวมทีมประจำวัน</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/50">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    ทั้งหมด
                  </div>
                  <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {teamItems.length}
                  </div>
                </div>
                <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-950/40">
                  <div className="text-sm text-blue-700 dark:text-blue-200">
                    เข้างานแล้ว
                  </div>
                  <div className="mt-2 text-3xl font-bold text-blue-700 dark:text-blue-100">
                    {checkedInCount}
                  </div>
                </div>
                <div className="rounded-lg bg-green-50 p-4 dark:bg-green-950/40">
                  <div className="text-sm text-green-700 dark:text-green-200">
                    ปิดงานแล้ว
                  </div>
                  <div className="mt-2 text-3xl font-bold text-green-700 dark:text-green-100">
                    {completedCount}
                  </div>
                </div>
                <div className="rounded-lg bg-amber-50 p-4 dark:bg-amber-950/40">
                  <div className="text-sm text-amber-700 dark:text-amber-200">
                    ลืมออกงาน
                  </div>
                  <div className="mt-2 text-3xl font-bold text-amber-700 dark:text-amber-100">
                    {missingCheckoutCount}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>ตัวกรอง</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="attendance-date">วันที่ทำงาน</Label>
                <Input
                  id="attendance-date"
                  type="date"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                />
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={refreshAttendance}
              >
                รีเฟรชข้อมูล
              </Button>
              <div className="rounded-lg bg-slate-50 p-3 text-sm text-gray-600 dark:bg-slate-900/50 dark:text-gray-300">
                คำขอแก้เวลา pending: {pendingExceptions.length}
              </div>
            </CardContent>
          </Card>
        </div>

        {isLoading && (
          <div className="py-12 text-center text-gray-600 dark:text-gray-300">
            กำลังโหลดข้อมูล attendance...
          </div>
        )}

        {(teamQuery.isError || exceptionsQuery.isError) && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {getErrorMessage(
              teamQuery.error || exceptionsQuery.error,
              "ไม่สามารถโหลดข้อมูล attendance ได้"
            )}
          </div>
        )}

        {!isLoading && !teamQuery.isError && !exceptionsQuery.isError && (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>สถานะรายคน</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {teamItems.length === 0 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      ไม่พบสมาชิกในขอบเขตที่คุณดูแล
                    </div>
                  )}

                  {teamItems.map((item) => {
                    const draft = getRecordDraft(item);

                    return (
                      <div
                        key={item.userId}
                        className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                              {item.firstName} {item.lastName}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {item.employeeId} • วันที่ {item.workDate}
                            </div>
                          </div>
                          <span
                            className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-sm font-medium ${getAttendanceStatusClass(
                              item.status
                            )}`}
                          >
                            {getAttendanceStatusLabel(item.status)}
                          </span>
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
                          <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-900/50">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Schedule
                            </div>
                            <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {item.schedule.startTime || "-"} -{" "}
                              {item.schedule.endTime || "-"}
                            </div>
                          </div>
                          <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-900/50">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Check-in
                            </div>
                            <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {formatAttendanceDateTime(item.record?.checkInAt)}
                            </div>
                          </div>
                          <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-900/50">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Check-out
                            </div>
                            <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {formatAttendanceDateTime(item.record?.checkOutAt)}
                            </div>
                          </div>
                          <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-900/50">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              เวลาทำงาน
                            </div>
                            <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {formatAttendanceMinutes(item.record?.workedMinutes)}
                            </div>
                          </div>
                        </div>

                        {item.record ? (
                          <div className="mt-4 space-y-3 rounded-lg border border-dashed border-gray-300 p-4 dark:border-gray-600">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label htmlFor={`check-in-${item.record.id}`}>
                                  แก้เวลาเข้างาน
                                </Label>
                                <Input
                                  id={`check-in-${item.record.id}`}
                                  type="datetime-local"
                                  value={draft.checkInAt}
                                  onChange={(event) =>
                                    setRecordDraft(
                                      item.record!.id,
                                      "checkInAt",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`check-out-${item.record.id}`}>
                                  แก้เวลาออกงาน
                                </Label>
                                <Input
                                  id={`check-out-${item.record.id}`}
                                  type="datetime-local"
                                  value={draft.checkOutAt}
                                  onChange={(event) =>
                                    setRecordDraft(
                                      item.record!.id,
                                      "checkOutAt",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`check-in-note-${item.record.id}`}>
                                  หมายเหตุเข้างาน
                                </Label>
                                <Input
                                  id={`check-in-note-${item.record.id}`}
                                  value={draft.checkInNote}
                                  onChange={(event) =>
                                    setRecordDraft(
                                      item.record!.id,
                                      "checkInNote",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`check-out-note-${item.record.id}`}>
                                  หมายเหตุออกงาน
                                </Label>
                                <Input
                                  id={`check-out-note-${item.record.id}`}
                                  value={draft.checkOutNote}
                                  onChange={(event) =>
                                    setRecordDraft(
                                      item.record!.id,
                                      "checkOutNote",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <Button
                              onClick={() =>
                                updateRecordMutation.mutate({
                                  recordId: item.record!.id,
                                  input: {
                                    checkInAt:
                                      fromDateTimeLocalValue(draft.checkInAt),
                                    checkOutAt:
                                      fromDateTimeLocalValue(draft.checkOutAt),
                                    checkInNote: draft.checkInNote || undefined,
                                    checkOutNote:
                                      draft.checkOutNote || undefined,
                                  },
                                })
                              }
                              disabled={updateRecordMutation.isPending}
                            >
                              {updateRecordMutation.isPending
                                ? "กำลังบันทึก..."
                                : "บันทึกการแก้ไข"}
                            </Button>
                          </div>
                        ) : (
                          <div className="mt-4 rounded-lg bg-gray-50 p-4 text-sm text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                            ยังไม่มี record สำหรับวันนี้ หากพนักงานลืมลงเวลา
                            ให้ใช้คำขอ exception เพื่อสร้างหรือแก้ไขข้อมูล
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            <div>
              <Card>
                <CardHeader>
                  <CardTitle>คำขอแก้เวลา</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {exceptionItems.length === 0 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      ยังไม่มีคำขอแก้เวลา
                    </div>
                  )}

                  {exceptionItems.map((exception) => {
                    const draft = getExceptionDraft(exception);

                    return (
                      <div
                        key={exception.id}
                        className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">
                              {exception.user?.firstName} {exception.user?.lastName}
                            </div>
                            <span
                              className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-medium ${getAttendanceExceptionStatusClass(
                                exception.status
                              )}`}
                            >
                              {getAttendanceExceptionStatusLabel(exception.status)}
                            </span>
                          </div>

                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {exception.user?.employeeId || "-"} •{" "}
                            {getAttendanceExceptionTypeLabel(exception.type)}
                          </div>

                          <div className="rounded-lg bg-slate-50 p-3 text-sm text-gray-700 dark:bg-slate-900/50 dark:text-gray-200">
                            {exception.reason}
                          </div>

                          <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                เวลาเข้า (requested)
                              </div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {formatAttendanceDateTime(
                                  exception.requestedCheckInAt
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                เวลาออก (requested)
                              </div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {formatAttendanceDateTime(
                                  exception.requestedCheckOutAt
                                )}
                              </div>
                            </div>
                          </div>

                          {exception.status === "pending" ? (
                            <div className="space-y-3 rounded-lg border border-dashed border-gray-300 p-3 dark:border-gray-600">
                              <div className="space-y-2">
                                <Label htmlFor={`exception-in-${exception.id}`}>
                                  เวลาเข้าเพื่ออนุมัติ
                                </Label>
                                <Input
                                  id={`exception-in-${exception.id}`}
                                  type="datetime-local"
                                  value={draft.checkInAt}
                                  onChange={(event) =>
                                    setExceptionDraft(
                                      exception.id,
                                      "checkInAt",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`exception-out-${exception.id}`}>
                                  เวลาออกเพื่ออนุมัติ
                                </Label>
                                <Input
                                  id={`exception-out-${exception.id}`}
                                  type="datetime-local"
                                  value={draft.checkOutAt}
                                  onChange={(event) =>
                                    setExceptionDraft(
                                      exception.id,
                                      "checkOutAt",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`exception-note-${exception.id}`}>
                                  หมายเหตุการพิจารณา
                                </Label>
                                <textarea
                                  id={`exception-note-${exception.id}`}
                                  value={draft.resolutionNote}
                                  onChange={(event) =>
                                    setExceptionDraft(
                                      exception.id,
                                      "resolutionNote",
                                      event.target.value
                                    )
                                  }
                                  rows={3}
                                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                />
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  onClick={() =>
                                    resolveExceptionMutation.mutate({
                                      exceptionId: exception.id,
                                      input: {
                                        status: "resolved",
                                        resolutionNote:
                                          draft.resolutionNote || undefined,
                                        checkInAt:
                                          fromDateTimeLocalValue(draft.checkInAt),
                                        checkOutAt:
                                          fromDateTimeLocalValue(draft.checkOutAt),
                                      },
                                    })
                                  }
                                  disabled={resolveExceptionMutation.isPending}
                                >
                                  อนุมัติและอัปเดตเวลา
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() =>
                                    resolveExceptionMutation.mutate({
                                      exceptionId: exception.id,
                                      input: {
                                        status: "rejected",
                                        resolutionNote:
                                          draft.resolutionNote || undefined,
                                      },
                                    })
                                  }
                                  disabled={resolveExceptionMutation.isPending}
                                >
                                  ปฏิเสธ
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                              ปิดคำขอเมื่อ {formatAttendanceDateTime(exception.resolvedAt)}
                              {exception.resolutionNote
                                ? ` • ${exception.resolutionNote}`
                                : ""}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

export default AttendanceManagementPage;
